/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PipelineMonitoring.tsx
 *     Feature         :   206-pipeline-monitoring-redesign
 *     Description     :   Two-tab monitoring section (Dashboard + Job Activities) driven
 *                         dynamically by the Universal Logging Engine (Spec 194). Replaces
 *                         the Spec-191 registry monitoring UI at the Phase-8 cutover.
 *     Constitution    :   Principle XXXVII (headers), XLV (design system: kit + tokens)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-30
 * =========================================================================================
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, ListChecks, SlidersHorizontal } from 'lucide-react';
import { PageLayout, Tabs, EmptyState } from '@/components/ui';
import type { TabItem } from '@/components/ui';
import { DashboardTab } from '@/components/operations/monitoring/DashboardTab';
import { JobActivitiesTab } from '@/components/operations/monitoring/JobActivitiesTab';
import { ManagementTab } from '@/components/operations/monitoring/ManagementTab';
import { useMonitoringConfig, pipelineMonitoringKeys } from '@/hooks/usePipelineMonitoring';

const TABS: TabItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
  { key: 'activities', label: 'Job Activities', icon: <ListChecks size={16} /> },
  { key: 'management', label: 'Management', icon: <SlidersHorizontal size={16} /> },
];

export default function PipelineMonitoring(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive the tab from the URL (single source of truth) so a same-page <Link>
  // — e.g. the dashboard's "View activity →" (?tab=activities&app=…) — actually
  // switches tabs. A one-time useState(initialTab) never re-read the query change.
  const rawTab = searchParams.get('tab');
  const tab = rawTab === 'activities' || rawTab === 'management' ? rawTab : 'dashboard';
  const { data: cfg } = useMonitoringConfig();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const onChange = (key: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const onRefresh = async (): Promise<void> => {
    setRefreshing(true);
    try { await qc.invalidateQueries({ queryKey: pipelineMonitoringKeys.all }); }
    finally { setRefreshing(false); }
  };

  return (
    <PageLayout
      helpId="auto.pipeline-monitoring"
      title="Pipeline Monitoring"
      description="Health and activity for every data pipeline, sourced live from the Universal Logging Engine."
      icon={<LayoutDashboard size={22} />}
      onRefresh={onRefresh}
      isRefreshing={refreshing}
    >
      {cfg && !cfg.v2Enabled ? (
        <EmptyState
          title="Pipeline Monitoring is disabled"
          message="Set global_settings.pipeline_monitoring_v2_enabled = true to enable this section."
        />
      ) : (
        <>
          <Tabs tabs={TABS} value={tab} onChange={onChange} />
          <div className="mt-4">
            {tab === 'dashboard' && <DashboardTab />}
            {tab === 'activities' && (
              // key by app so navigating to a different ?app (e.g. another "View activity"
              // link or an alert deep-link) remounts + re-filters JobActivitiesTab.
              <JobActivitiesTab
                key={searchParams.get('app') ?? 'all'}
                initialApp={searchParams.get('app') ?? undefined}
              />
            )}
            {tab === 'management' && <ManagementTab />}
          </div>
        </>
      )}
    </PageLayout>
  );
}
